import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CircleAlert, Eye, EyeOff, Loader2, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/auth.context';
import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api-errors';
import { SessionStartError } from '@/lib/auth-errors';
import { LOGIN_ROUTE, POST_LOGIN_ROUTE } from '@/lib/auth-routes';
import { meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE } from '@/lib/password-policy';
import memberInvitationService, { type MemberInvitationDetails } from '@/services/member-invitation.service';

const INVALID_INVITE_MESSAGE =
  'Este link não é mais válido. Peça ao titular um novo convite.';

const apiDetail = (error: unknown): unknown =>
  (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;

type ValidationState =
  | { status: 'loading' }
  | { status: 'invalid'; message: string }
  | { status: 'valid'; details: MemberInvitationDetails };

interface PasswordInputProps {
  id: string;
  label: string;
  toggleLabel: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  error?: string;
}

function PasswordInput({ id, label, toggleLabel, value, disabled, onChange, error }: Readonly<PasswordInputProps>) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          autoComplete="new-password"
          className="h-11 pr-11"
        />
        <button
          type="button"
          aria-label={toggleLabel}
          onClick={() => setVisible((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';
  const navigate = useNavigate();
  const { acceptMemberInvitation } = useAuth();

  const [validation, setValidation] = useState<ValidationState>({ status: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    if (!token) {
      setValidation({ status: 'invalid', message: INVALID_INVITE_MESSAGE });
      return;
    }

    memberInvitationService
      .validate(token)
      .then((details) => active && setValidation({ status: 'valid', details }))
      .catch((error: unknown) => {
        if (!active) return;
        setValidation({ status: 'invalid', message: getApiErrorMessage(apiDetail(error), INVALID_INVITE_MESSAGE) });
      });

    return () => {
      active = false;
    };
  }, [token]);

  const passwordError = password && !meetsPasswordPolicy(password) ? PASSWORD_POLICY_MESSAGE : undefined;
  const confirmationError =
    confirmation && confirmation !== password ? 'As senhas não coincidem.' : undefined;
  const canSubmit =
    meetsPasswordPolicy(password) && password === confirmation && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await acceptMemberInvitation(token, password, confirmation);
      navigate(POST_LOGIN_ROUTE, { replace: true });
    } catch (error: unknown) {
      if (error instanceof SessionStartError) {
        toast({
          title: 'Senha criada com sucesso',
          description: 'Entre com seu e-mail e a senha que você acabou de definir.',
        });
        navigate(LOGIN_ROUTE, { replace: true });
        return;
      }
      toast({
        title: 'Não foi possível aceitar o convite',
        description: getApiErrorMessage(apiDetail(error), INVALID_INVITE_MESSAGE),
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (validation.status === 'loading') {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p>Validando convite…</p>
        </div>
      );
    }

    if (validation.status === 'invalid') {
      return (
        <div className="space-y-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <CircleAlert className="h-7 w-7 text-red-600" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">Convite indisponível</h1>
            <p className="text-gray-600">{validation.message}</p>
          </div>
          <Button asChild variant="outline" className="w-full h-11">
            <Link to={LOGIN_ROUTE}>Ir para o login</Link>
          </Button>
        </div>
      );
    }

    const { details } = validation;
    return (
      <>
        <div className="space-y-3 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <UserPlus className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Bem-vindo(a), {details.member_name}!</h1>
          <p className="text-gray-600">
            <strong>{details.owner_name}</strong> convidou você para acessar a conta dele(a) no Librattum.
            Crie sua senha para entrar.
          </p>
          <p className="text-sm text-gray-500">
            Seu login será <span className="font-medium text-gray-900">{details.email}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <PasswordInput
            id="invitePassword"
            label="Senha"
            toggleLabel="Mostrar senha"
            value={password}
            disabled={isSubmitting}
            onChange={setPassword}
            error={passwordError}
          />
          <PasswordInput
            id="inviteConfirmPassword"
            label="Confirmar senha"
            toggleLabel="Mostrar confirmação"
            value={confirmation}
            disabled={isSubmitting}
            onChange={setConfirmation}
            error={confirmationError}
          />
          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-11 font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar senha e acessar
          </Button>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 brand-light-surface">
      <div className="w-full max-w-md space-y-8 rounded-2xl bg-white p-8 shadow-lg">
        <img src="/logo-librattum.png" alt="Librattum" className="mx-auto h-10" />
        {renderContent()}
      </div>
    </div>
  );
}
